import { Field, FieldProps, FieldRenderProps } from 'react-final-form';

import { MultipleTextInput as Input } from '@vk-hr-tek/ui/input';

/**
 * Компонент для ввода нескольких текстовых значений.
 *
 * @public
 * @param id - Уникальный идентификатор элемента.
 * @param name - Имя поля формы.
 * @param type - Тип поля ввода.
 * @param label - Метка поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param placeholder - Подсказка для поля ввода.
 * @param max - Максимальное количество текстовых полей.
 * @param onChange - Коллбэк функция, вызываемая при изменении значений.
 * @param tooltip - Текст всплывающей подсказки.
 * @param showError - Флаг для отображения ошибок.
 * @param required - Флаг обязательности поля.
 * @param disabled - Флаг для отключения поля.
 * @param loading - Флаг состояния загрузки.
 * @param testId - Идентификатор для тестирования.
 * @param rest - Остальные пропсы, передаваемые в {@link Field}.
 * @returns Компонент поля ввода с несколькими текстовыми значениями.
 */
export const MultipleTextInput = ({
  id,
  name,
  type,
  label,
  labelIcon,
  placeholder,
  max = 3,
  onChange,
  tooltip,
  showError = true,
  required = false,
  disabled = false,
  loading = false,
  testId,
  ...rest
}: FieldProps<string | undefined, FieldRenderProps<string | undefined>>) => {
  return (
    <Field name={name} {...rest}>
      {({ input, meta }) => {
        const values = Array.isArray(input.value) ? [...input.value] : [];

        const handleChange = (index: number, value: string) => {
          values[index] = value;
          input.onChange(values);

          if (onChange) {
            onChange(values);
          }
        };

        return (
          <Input
            {...input}
            id={id}
            testId={testId}
            label={label}
            labelIcon={labelIcon}
            required={required}
            disabled={disabled}
            values={values}
            onChange={handleChange}
            type={type}
            error={meta.touched && !!meta.error}
            helperText={showError && meta.touched && meta.error}
            placeholder={placeholder}
            tooltip={tooltip}
            max={max}
            loading={loading}
          />
        );
      }}
    </Field>
  );
};
